/**
 * Shared derivation for the assistant activity trace.
 *
 * Native (`message.tsx`) and web (`thread.web.tsx`) render the trace with
 * different primitives but must agree on *what* the steps are — otherwise the
 * same conversation narrates itself differently depending on where you opened
 * it. All the decisions live here; the platform files only draw.
 *
 * The trace is derived, never invented: a step exists because a tool part
 * exists, and its tense comes from that part's real `stage`. The only two steps
 * with no tool behind them are the honest waits — before the model has produced
 * anything, and after the last tool while the answer is still forming.
 */
import type { TFunction } from 'i18next';

export type TracePart = {
  type: string;
  toolName?: string;
  toolCallId?: string;
  stage?: string;
  result?: unknown;
  isError?: boolean;
  text?: string;
  streamKind?: string;
};

/**
 * Tool calls that should never produce a step.
 *
 * `HIDDEN_TOOLS` covers working-memory plumbing and workflow wrappers. On top of
 * that, synthetic client-side tools are prefixed `__` (e.g. the `__subscriptionBlocked`
 * card the stream fabricates on a 403) — those are UI constructs, not work the
 * agent did, so narrating them would be a lie.
 */
export function isVisibleToolPart(part: TracePart, hiddenTools: ReadonlySet<string>): boolean {
  if (part.type !== 'tool-call' || !part.toolName) return false;
  if (hiddenTools.has(part.toolName)) return false;
  if (part.toolName.startsWith('__')) return false;
  return true;
}

export function visibleToolParts(
  parts: readonly TracePart[],
  hiddenTools: ReadonlySet<string>,
): TracePart[] {
  return parts.filter((part) => isVisibleToolPart(part, hiddenTools));
}

/**
 * How much answer text is on screen: every text part's characters with the
 * whitespace taken out. Whitespace alone paints nothing, so a turn whose first
 * delta is a line break has not started its answer. The count only grows as
 * text streams in, however the runtime splits it into settled and live parts,
 * which is what lets the phase compare it across renders.
 */
export function visibleTextLength(parts: readonly TracePart[]): number {
  let length = 0;
  for (const part of parts) {
    if (part.type === 'text' && typeof part.text === 'string') {
      length += part.text.replace(/\s+/g, '').length;
    }
  }
  return length;
}

/**
 * "Thinking…": the gap between hitting send and the first thing the user can
 * see. Hidden plumbing (working memory, workflow wrappers) and blank text do
 * not end it: they draw nothing, and ending the wait on them left the screen
 * empty until the first real step or word (the dead gap).
 */
export function shouldShowThinking(
  parts: readonly TracePart[],
  isRunning: boolean,
  hiddenTools: ReadonlySet<string> = new Set(),
): boolean {
  return resolveTracePhase(parts, isRunning, hiddenTools) === 'thinking';
}

/**
 * "Writing…": tools have finished but the answer has not started.
 *
 * Without this the trace goes quiet at exactly the point a multi-step turn is
 * slowest: the model re-reads every tool result before composing the answer.
 * Suppressed while a tool is still running, because that tool's own step is
 * already saying what is happening.
 */
export function shouldShowWriting(
  parts: readonly TracePart[],
  isRunning: boolean,
  hiddenTools: ReadonlySet<string>,
  textBeforeLastResult = 0,
): boolean {
  return resolveTracePhase(parts, isRunning, hiddenTools, { textBeforeLastResult }) === 'writing';
}

/**
 * Whether the trace starts folded.
 *
 * A finished turn that is no longer the last one folds to a single line: the
 * steps were useful while the work was happening, and keeping every one of them
 * expanded turns a long conversation into a wall of grey. The newest answer
 * always stays open so the user can see what just happened without a click.
 */
export function shouldStartExpanded(isRunning: boolean, isLast: boolean): boolean {
  return isRunning || isLast;
}

/**
 * Label for the folded trace: "3 steps".
 *
 * Deliberately just the count. An earlier draft appended the step names
 * ("3 steps · Read CV, searched jobs") but on a narrow column that truncates
 * mid-word, which reads as breakage rather than as a summary.
 */
export function traceSummaryLabel(stepCount: number, t: TFunction): string {
  return t('chat.activity.summary', { count: stepCount });
}

/**
 * A step that was still running when the message stopped running never got a
 * result — the stream aborted or errored underneath it. Narrating it as
 * "Searching jobs…" forever (and leaving its skeleton card on screen) would be
 * a lie, so once the turn is over it reads as a step that didn't finish. A
 * step waiting on the user's approval is genuinely still waiting and is left
 * alone.
 */
export function settleStage(stage: string | undefined, isRunning: boolean): string | undefined {
  if (isRunning) return stage;
  if (stage === undefined || stage === 'running') return 'failed';
  return stage;
}

// ---------------------------------------------------------------------------
// Trace header
//
// The trace is one expandable block with a header line that reads as the
// turn's current state: a shimmering verb while work is happening, a settled
// summary once it is done. The four phases below are the only states it can
// be in, and every one of them is derived from the parts on the wire.
// ---------------------------------------------------------------------------

export type TracePhase =
  /** Nothing visible has arrived yet: no step, no words. */
  | 'thinking'
  /** At least one visible tool is still running or waiting on the user. */
  | 'working'
  /** Every tool has returned, the answer has not started. */
  | 'writing'
  /** The turn is over (or was never live on this device). */
  | 'settled';

function isActiveStage(stage: string | undefined): boolean {
  return stage === 'running' || stage === 'pending_approval' || stage === undefined;
}

/**
 * The turn's phase. The rule behind every branch: while the turn runs there is
 * always either something moving (the loader, a running step, "Writing…") or
 * the answer itself, never an empty gap between them.
 *
 * `textBeforeLastResult` is the answer text that was already on screen when
 * the last tool result landed (see `useTracePhase`). Text the model wrote
 * before calling a tool does not mean the answer has started: the closing
 * sentence needs another model round trip, and folding the trace to
 * "Ran N steps" while it forms read as a stalled screen. Without it, any text
 * counts.
 */
export function resolveTracePhase(
  parts: readonly TracePart[],
  isRunning: boolean,
  hiddenTools: ReadonlySet<string>,
  options: { textBeforeLastResult?: number } = {},
): TracePhase {
  if (!isRunning) return 'settled';
  const tools = visibleToolParts(parts, hiddenTools);
  const text = visibleTextLength(parts);
  if (tools.length === 0 && text === 0) return 'thinking';
  if (tools.some((part) => isActiveStage(part.stage))) return 'working';
  // Prose with no tool behind it: nothing to narrate.
  if (tools.length === 0) return 'settled';
  if (text <= (options.textBeforeLastResult ?? 0)) return 'writing';
  // The answer is streaming with every tool settled: the trace is done
  // narrating even though the message as a whole is still running.
  return 'settled';
}

/**
 * Changes whenever a visible step starts or settles, and only then: the
 * moment `useTracePhase` takes its text baseline.
 */
export function traceToolsKey(
  parts: readonly TracePart[],
  hiddenTools: ReadonlySet<string>,
): string {
  return visibleToolParts(parts, hiddenTools)
    .map((part, index) => `${part.toolCallId ?? index}:${isActiveStage(part.stage) ? 'active' : 'done'}`)
    .join('|');
}

/** What `useTracePhase` carries from one render of a live message to the next. */
export type TracePhaseMemory = {
  /** `traceToolsKey` at the last render. */
  key: string;
  /** `visibleTextLength` at the last render. */
  text: number;
  /** The answer text on screen just before the steps last changed. */
  baseline: number;
};

export const INITIAL_TRACE_PHASE_MEMORY: TracePhaseMemory = { key: '', text: 0, baseline: 0 };

/**
 * One render of a live message: the phase, and the memory for the next
 * render. The baseline is read from the previous render, not this one, so a
 * tool result and the first words that land in the same update still count
 * as an answer that has started.
 */
export function advanceTracePhase(
  memory: TracePhaseMemory,
  parts: readonly TracePart[],
  isRunning: boolean,
  hiddenTools: ReadonlySet<string>,
): { phase: TracePhase; memory: TracePhaseMemory } {
  const key = traceToolsKey(parts, hiddenTools);
  const text = visibleTextLength(parts);
  const baseline = key === memory.key ? memory.baseline : memory.text;
  return {
    phase: resolveTracePhase(parts, isRunning, hiddenTools, { textBeforeLastResult: baseline }),
    memory: { key, text, baseline },
  };
}

/**
 * Whether the step list is open.
 *
 * Mirrors the reference behaviour: the trace unfolds by itself while the work
 * is happening, folds once it settles, and stays a click away after that. A
 * manual toggle always wins over the automatic state.
 */
export function resolveTraceExpanded(
  phase: TracePhase,
  manualExpanded: boolean | null,
): boolean {
  if (manualExpanded != null) return manualExpanded;
  return phase === 'working' || phase === 'writing';
}

/**
 * The header label.
 *
 * Active phases share the shimmer treatment and read as verbs; the settled
 * label carries the step count and, when this device watched the run, how
 * long it took. A rehydrated turn has no duration and simply omits it.
 */
export function resolveTraceHeaderLabel(params: {
  phase: TracePhase;
  stepCount: number;
  durationLabel: string | null;
  t: TFunction;
}): string {
  const { phase, stepCount, durationLabel, t } = params;
  if (phase === 'thinking') return t('chat.activity.phase.thinking');
  if (phase === 'working') return t('chat.activity.header.working');
  if (phase === 'writing') return t('chat.activity.phase.writing');
  if (durationLabel) {
    return t('chat.activity.header.doneTimed', { count: stepCount, duration: durationLabel });
  }
  return t('chat.activity.header.done', { count: stepCount });
}
